import {
  BOND_SECURITY_KEY_FIELD,
  BOND_SECURITY_SEARCH_FIELDS,
  BondSecurityField,
  IBondSecurity,
} from "@/lib/interfaces and Enums/ENUMBondSecurity";
import {
  DEFAULT_SEARCH_LIMIT,
  ISearchResult,
  findItemByFieldValue,
  searchItemsByFields,
} from "@/lib/referenceDataSearchHelper";
import bondSecuritiesStore from "./bondSecuritiesStore";
import { loadReferenceDataOnce } from "./referenceDataLoadHelper";

// LQC-2254 - GET /Securities/bonds, called on demand when the Leg Creation or
// the Prices widget mounts. Loading and Loaded both short-circuit the fetch, so the two
// widgets share one request. This payload is large, so it is deliberately kept off the
// app-load path: a user who adds neither widget never pays for it. See
// widgets/tradeFromScratch/README.md.

export const BOND_SECURITIES_ENDPOINT = "/Securities/bonds";

// ----- Pure transform -------------------------------------------------------

// Unwrap { bonds: [...] }. Returns null for anything else so the loader can flag a
// contract mismatch rather than seeding an empty list.
export function extractBondSecurities(response: any): IBondSecurity[] | null {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.bonds)) return response.bonds;
  return null;
}

// ----- Feed -----------------------------------------------------------------

export async function getAllBondSecurities(
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  const store = bondSecuritiesStore.getState();
  await loadReferenceDataOnce<IBondSecurity>({
    url: BOND_SECURITIES_ENDPOINT,
    source: "bondSecuritiesStoreACTIONS.getAllBondSecurities",
    getAccessTokenSilently,
    readStatus: () => bondSecuritiesStore.getState().bondSecuritiesStatus,
    setStatus: store.setBondSecuritiesStatus,
    setItems: store.setBondSecurities,
    setError: store.setBondSecuritiesError,
    extractItems: extractBondSecurities,
    forceReload,
  });
}

// ----- Search / lookup ------------------------------------------------------

// Type-to-find over ISIN, security name and issuer. An empty term returns the head of
// the list so a dropdown has something to show before the user types. The result carries
// totalMatches so the caller can show how much was cut off by the limit.
export function searchBondSecurities(
  term: string,
  limit: number = DEFAULT_SEARCH_LIMIT,
): ISearchResult<IBondSecurity> {
  return searchItemsByFields<IBondSecurity>({
    items: bondSecuritiesStore.getState().bondSecurities,
    term,
    fields: BOND_SECURITY_SEARCH_FIELDS,
    limit,
  });
}

// Confirms a chosen bond really exists in our data before it is sent.
export function findBondSecurityByIsin(
  isin: string,
): IBondSecurity | undefined {
  return findItemByFieldValue<IBondSecurity>(
    bondSecuritiesStore.getState().bondSecurities,
    BOND_SECURITY_KEY_FIELD,
    isin,
  );
}

// Users search by the readable name far more often than by ISIN, so resolving a name
// back to its security is a first-class lookup, not a convenience.
export function findBondSecurityByName(
  securityName: string,
): IBondSecurity | undefined {
  return findItemByFieldValue<IBondSecurity>(
    bondSecuritiesStore.getState().bondSecurities,
    BondSecurityField.securityName,
    securityName,
  );
}

// Validation gate for the send path - an ISIN we cannot resolve must not reach the
// back end.
export function isKnownBondIsin(isin: string): boolean {
  return findBondSecurityByIsin(isin) !== undefined;
}
